import { Avatar, IconButton, Input, List, ListItem, ListItemIcon, ListItemText } from "@/components/ui";
import { useAppDispatch, useAppSelector } from "@/config/store";
import { IEdoChatMessage } from "@/shared/model/edo-chat-message.model";
import { IEdoChat } from "@/shared/model/edo-chat.model";
import { EdoChatType } from "@/shared/model/enumerations/edo-chat-type.model";
import { AuthenticationState } from "@/shared/reducers/authentication";
import { ChatState } from "@/shared/reducers/chat.reducer";
import { checkAllMessagesByChat } from "@/shared/reducers/entities/edo-chat-message.reducer";
import { Send, User, Users } from "lucide-react";
import axios from "axios";
import { SocketContext } from "context/socket";
import { useContext, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { MyMessage } from "../../components/MyMessage";
import { OtherMessage } from "../../components/OtherMessage";

const Chat = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const messageAreaRef = useRef(null);
  const socket = useContext(SocketContext);
  const params = useParams<{ id: string }>();
  const [messageToSend, setMessageToSend] = useState<string>("");
  const [messages, setMessages] = useState<IEdoChatMessage[]>([]);
  const { account } = useAppSelector<AuthenticationState>((state) => state.authentication);
  const { selectedParticipation } = useAppSelector<ChatState>(state => state.chat);

  const sendChatMessage = (message: string, chat: IEdoChat) => {
    socket.emit('message', {
      message,
      chat
    });
  };

  const handleOnMessageEvent = (message: IEdoChatMessage) => {
    if (String(message.chat.id) === params.id) {
      setMessages([...messages, message]);
      dispatch(checkAllMessagesByChat(params.id));
    }
  };

  const sendMessage = () => {
    if (!messageToSend) return;

    sendChatMessage(messageToSend, { id: Number(params.id) });
    setMessageToSend("");
  };

  useEffect(() => {
    const getAllMessages = async () => {
      const response = await axios.get('/chat-messages', {
        params: {
          page: 0,
          limit: 20,
          sortBy: 'createdAt:ASC',
          'filter.chat.id': '$eq:' + params.id
        }
      });

      setMessages([...response.data.data]);
    };

    if (params.id) {
      getAllMessages();
      dispatch(checkAllMessagesByChat(params.id));
    }
  }, [params.id]);

  useEffect(() => {
    // const executeScroll = () => messageAreaRef.current.scrollIntoView({ behaviour: 'smooth' });
    const executeScroll = () => {
      messageAreaRef.current.scrollTop = messageAreaRef.current.scrollHeight;
    };

    executeScroll();
  }, [messages]);

  useEffect(() => {
    socket.on('message', handleOnMessageEvent);
    return function cleanup() {
      socket.off('message', handleOnMessageEvent);
    };
  });

  return (
    <div className="flex h-full flex-col">
      {selectedParticipation ? (
        <>
          <List>
            <ListItem
              key={selectedParticipation.chat.id}
              className="flex cursor-pointer items-center gap-3 px-4 py-3 transition-colors hover:bg-primary/10"
              onClick={() => navigate(`/chat/${params.id}/info`)}
            >
              <ListItemIcon>
                <Avatar className="h-10 w-10">
                  {selectedParticipation.chat.type === EdoChatType.CHAT ? (
                    <User className="h-5 w-5" />
                  ) : (
                    <Users className="h-5 w-5" />
                  )}
                </Avatar>
              </ListItemIcon>
              <ListItemText
                primary={
                  selectedParticipation.chat.type === EdoChatType.CHAT
                    ? selectedParticipation.title
                    : selectedParticipation.chat.title
                }
              />
            </ListItem>
          </List>

          <hr className="border-[var(--color-mockup-border)]" />
        </>
      ) : null}
      <div className="flex-1 overflow-y-auto" ref={messageAreaRef}>
        <List>
          {Array.from(messages).sort((a, b) => (new Date(a.createdAt)).getTime() - (new Date(b.createdAt)).getTime())
            .map((message) => {
              if (message.user.id === account.id) {
                return <div className="mb-4 px-4" key={message.id}><MyMessage messaggio={message} /></div>;
              } else {
                return <div className="mb-4 px-4" key={message.id}><OtherMessage messaggio={message} /></div>;
              }
            })}
        </List>
      </div>
      <hr className="border-[var(--color-mockup-border)]" />
      <div className="flex shrink-0 items-center justify-between gap-4 p-4">
        <div className="mr-4 flex-1">
          <Input
            placeholder="Scrivi un messaggio"
            value={messageToSend}
            onChange={(e) => setMessageToSend(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          />
        </div>
        <IconButton
          variant="primary"
          onClick={() => sendMessage()}
          aria-label="Invia messaggio"
        >
          <Send className="h-5 w-5" />
        </IconButton>
      </div>
    </div>
  );
};

export default Chat;
